import React, { useEffect, useState } from "react";
import { Button } from "../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from "../../design/dialog";
import { Input } from "../../design/input";
import { Field, Fieldset, FieldGroup, Label } from "../../design/fieldset";
import { Textarea } from "../../design/textarea";
import { useDocument } from "../../contexts/DocumentContext";
import ChooseDocumentType from "./components/ChooseDocumentType";
import { PlusIcon } from "@heroicons/react/20/solid";
import { DocumentGenerationMethod } from "../../models/DocumentGenerationMethod";
import { DocumentType } from "../../models/DocumentType";
import ChooseGenerationMethod from "./components/ChooseGenerationMethod";
import { Heading, Subheading } from "../../design/heading";
import { Text } from "../../design/text";
import { Link } from "../../design/link";

interface Props {
  type?: DocumentType;
  generationMethod?: DocumentGenerationMethod;
  className?: string;
  children?: React.ReactNode;
}

const CreateDocumentButton: React.FC<Props> = (props) => {
  const { createDocument } = useDocument();
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState("");
  const [type, setType] = useState<DocumentType | undefined>(props.type);
  const [generationMethod, setGenerationMethod] = useState<
    DocumentGenerationMethod | undefined
  >(props.generationMethod);
  const [description, setDescription] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (!type) return;
    if (type === "parenting_plan") {
      setName("Parenting Plan");
    } else {
      setName("");
    }
  }, [type]);

  const handleSubmit = async () => {
    if (!name || !type) return;
    setIsUploading(true);
    try {
      await createDocument({
        name,
        description,
        type,
        generationMethod: generationMethod ?? "manual",
      });
      clearForm();
    } catch (err) {
      console.error("Error creating document:", err);
      alert("Something went wrong. Please try again.");
    }
    setIsUploading(false);
  };

  const clearForm = () => {
    setName("");
    setDescription("");
    setType(undefined);
    setGenerationMethod(undefined);
    setIsOpen(false);
    setIsUploading(false);
  };

  return (
    <>
      <Button
        className={props.className}
        type="button"
        onClick={() => setIsOpen(true)}
      >
        {props.children ?? (
          <>
            <PlusIcon className="w-4 h-4" />
            <span className="hidden sm:block">Create Document</span>
          </>
        )}
      </Button>

      <Dialog open={isOpen} onClose={setIsOpen}>
        {!type && (
          <DialogTitle>
            What type of document do you want to create?
          </DialogTitle>
        )}
        {type && !generationMethod && (
          <DialogTitle>How would you like to create this document?</DialogTitle>
        )}
        {type && generationMethod && <DialogTitle>Create Document</DialogTitle>}
        {type === "parenting_plan" && (
          <DialogDescription>
            <div className="p-4 border border-blue-300 rounded-md bg-blue-50">
              <Subheading level={4} className="mb-1 text-blue-900">
                Want expert help crafting your plan?
              </Subheading>
              <p className="mb-3 text-xs text-blue-800">
                A professional can guide you through a complete, court-ready parenting plan — especially helpful if your situation is complex or contested.
              </p>
              <Button
              color="blue"
                href="/professionals"
              >
                Work with a professional
              </Button>
            </div>
          </DialogDescription>
        )}
        <DialogBody>
          {!type && <ChooseDocumentType type={type} setType={setType} />}
          {type && !generationMethod && (
            <ChooseGenerationMethod
              generationMethod={generationMethod}
              setGenerationMethod={setGenerationMethod}
            />
          )}
          {type && generationMethod && (
            <Fieldset>
              <FieldGroup>
                <Field>
                  <Label>Name</Label>
                  <Input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Document name"
                  />
                </Field>
                <Field>
                  <Label>Description</Label>
                  <Textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Brief description"
                  />
                </Field>
              </FieldGroup>
            </Fieldset>
          )}
        </DialogBody>

        <DialogActions>
          {!type && !generationMethod && (
            <Button plain onClick={clearForm}>
              Cancel
            </Button>
          )}
          {type && !generationMethod && (
            <Button plain onClick={() => setType(undefined)}>
              Back
            </Button>
          )}
          {type && generationMethod && (
            <Button plain onClick={() => setGenerationMethod(undefined)}>
              Back
            </Button>
          )}
          {type && generationMethod && (
            <Button
              color="green"
              disabled={!name || !type || isUploading}
              onClick={handleSubmit}
            >
              {isUploading ? "Creating..." : "Create"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateDocumentButton;
